/**
 * Geometry helpers for the small contextual popover (e.g. the translation
 * language picker). Kept as a pure function so the clamping logic — keeping
 * the menu fully on screen and flipping it above the trigger when there is no
 * room below — can be unit-tested without mounting React Native.
 */

/** Approximate popover width; mirrors TranslateLangPicker's minWidth + slack. */
export const POPOVER_WIDTH = 200
/** Gap kept between the popover and any screen edge. */
export const POPOVER_MARGIN = 8
/** Approximate height of a single language row. */
export const POPOVER_ROW_HEIGHT = 44
/** Card vertical padding (top + bottom). */
export const POPOVER_VERTICAL_PADDING = 8
/** Gap between the trigger and the popover. */
export const POPOVER_TRIGGER_GAP = 4

export interface PopoverGeometryInput {
  /** Trigger x in window coordinates. */
  triggerX: number
  /** Trigger y in window coordinates. */
  triggerY: number
  /** Trigger height. */
  triggerHeight: number
  /** Number of rows the popover will render. */
  rowCount: number
  screenWidth: number
  screenHeight: number
}

export interface PopoverPosition {
  x: number
  y: number
}

export const computePopoverPosition = ({
  triggerX,
  triggerY,
  triggerHeight,
  rowCount,
  screenWidth,
  screenHeight,
}: PopoverGeometryInput): PopoverPosition => {
  const menuHeight = rowCount * POPOVER_ROW_HEIGHT + POPOVER_VERTICAL_PADDING

  // Clamp horizontally so the labels are never clipped on the right edge.
  const maxLeft = Math.max(POPOVER_MARGIN, screenWidth - POPOVER_WIDTH - POPOVER_MARGIN)
  const x = Math.min(Math.max(POPOVER_MARGIN, triggerX), maxLeft)

  // Open below the trigger; flip above when there isn't enough room.
  const below = triggerY + triggerHeight + POPOVER_TRIGGER_GAP
  const y =
    below + menuHeight > screenHeight - POPOVER_MARGIN
      ? Math.max(POPOVER_MARGIN, triggerY - menuHeight - POPOVER_TRIGGER_GAP)
      : below

  return { x, y }
}
